import { useEffect, useState } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { File, FileClock, MessagesSquare } from "lucide-react"

import { AgentRunnerConversationHistoryButton } from "@components/buttons/AgentRunnerConversationHistoryButton"
import { DashboardDocumentButton } from "@components/buttons/DashboardDocumentButton"

import { useTabNotification } from "@hooks/use-tab-notification"

import { SearchableExecutionRunRef } from "@models/executions"

import { useWatchSearchResult } from "@plugins/providers/agent/AgentHistoryContext"

import { useUserSession } from "@session/UserSession"

import { Card, CardContent } from "@ui/card"

export function DashboardPanel() {
    const { flashTitle } = useTabNotification()
    const { client, user } = useUserSession()
    const { search, loading, runs } = useWatchSearchResult()

    const username = user?.name.split(" ")[0] || "User"

    const [documentsInStore, setDocumentsInStore] = useState<number>(0)
    const [documents, setDocuments] = useState<ContentObjectItem[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [activeConversations, setActiveConversations] = useState<SearchableExecutionRunRef[]>([])
    const [previousConversations, setPreviousConversations] = useState<SearchableExecutionRunRef[]>([])

    const searchDocuments = () => {
        setIsLoading(true)
        client.objects.computeFacets({ facets: [] }).then((response) => {
            setDocumentsInStore(Array.isArray(response.total) ? response.total.length : response.total || 0)
        })

        client.objects
            .list({ limit: 50 })
            .then(setDocuments)
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleSearch = () => {
        searchDocuments()
        search.search()
    }

    useEffect(() => {
        const toReviewConversations = activeConversations.filter((run) => run.status === "to_review")
        if (toReviewConversations.length) {
            flashTitle(`${toReviewConversations.length} Conversation${toReviewConversations.length > 1 ? "s" : ""} to review!`)
        }
    }, [activeConversations])

    useEffect(() => {
        setActiveConversations(search.getActiveConversations(runs))
        setPreviousConversations(search.getPreviousConversations(runs))
    }, [runs, search])

    useEffect(() => {
        handleSearch()

        const interval = setInterval(
            () => {
                handleSearch()
            },
            2 * 60 * 1000
        )

        return () => clearInterval(interval)
    }, [])

    return (
        <div className="flex flex-col items-center justify-center w-full h-full max-w-[80%] mx-auto items-center justify-center gap-4">
            <div className="flex flex-row items-start w-full">
                <p className="text-center text-2xl font-medium tracking-tight">
                    Hey <span className="text-fuchsia-500 hover:cursor-pointer hover:underline">{username}</span>, good to have you back
                </p>
            </div>
            <div className="grid grid-cols-3 gap-4 w-full">
                <Card className="hover:shadow-lg">
                    <CardContent className="p-3 flex flex-col items-start gap-2">
                        <p className="text-base text-muted-foreground">Content</p>
                        <p className="text-lg font-bold">{documentsInStore}</p>
                    </CardContent>
                </Card>
                <Card className="hover:shadow-lg">
                    <CardContent className="p-3 flex flex-row items-start justify-between gap-2">
                        <div className="flex flex-1 flex-col items-start gap-2">
                            <p className="text-base text-muted-foreground">Ready to Review</p>
                            <p className="text-lg font-bold">{activeConversations.filter((run) => run.status === "to_review").length}</p>
                        </div>
                        <div className="flex flex-1 flex-col items-start gap-2">
                            <p className="text-base text-muted-foreground">In Progress</p>
                            <p className="text-lg font-bold">{activeConversations.filter((run) => run.status === "in_progress").length}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="hover:shadow-lg">
                    <CardContent className="p-3 flex flex-col items-start gap-2">
                        <p className="text-base text-muted-foreground">Previous</p>
                        <p className="text-lg font-bold">{previousConversations.length}</p>
                    </CardContent>
                </Card>
            </div>
            <div className="grid grid-cols-3 gap-4 w-full">
                <Card className="hover:shadow-lg">
                    <CardContent className="p-4">
                        <div className="w-full h-[400px] overflow-hidden flex flex-col">
                            <div className="flex flex-row items-center w-full gap-1 flex-shrink-0">
                                <File className="size-4 text-base text-muted-foreground" />
                                <p className="text-base text-muted-foreground">Content</p>
                            </div>
                            <div className="w-full flex-1 overflow-y-auto">
                                <div className="flex flex-col items-start w-full gap-2">{documents.length > 0 ? documents.map((item) => <DashboardDocumentButton key={item.id} document={item} />) : <p className="text-sm text-muted-foreground">{isLoading ? "Loading Content." : "No Content found."}</p>}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="hover:shadow-lg">
                    <CardContent className="p-4">
                        <div className="w-full h-[400px] overflow-hidden flex flex-col">
                            <div className="flex flex-row items-center w-full gap-1 flex-shrink-0">
                                <MessagesSquare className="size-4 text-base text-muted-foreground" />
                                <p className="text-base text-muted-foreground">Active Conversation</p>
                            </div>
                            <div className="w-full flex-1 overflow-y-auto">
                                <div className="flex flex-col items-start w-full gap-2">{activeConversations.length > 0 ? activeConversations.map((item) => <AgentRunnerConversationHistoryButton key={item.run.id} searchableRun={item} />) : <p className="text-sm text-muted-foreground">{loading ? "Loading active conversations." : "No active conversations found."}</p>}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="hover:shadow-lg">
                    <CardContent className="p-4">
                        <div className="w-full h-[400px] overflow-hidden flex flex-col">
                            <div className="flex flex-row items-center w-full gap-1 flex-shrink-0">
                                <FileClock className="size-4 text-base text-muted-foreground" />
                                <p className="text-base text-muted-foreground">Previous Conversations</p>
                            </div>
                            <div className="w-full flex-1 overflow-y-auto">
                                <div className="flex flex-col items-start w-full gap-2">{previousConversations.length > 0 ? previousConversations.map((item) => <AgentRunnerConversationHistoryButton key={item.run.id} searchableRun={item} />) : <p className="text-sm text-muted-foreground">{loading ? "Loading previous conversations." : "No previous conversations found."}</p>}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
